import { pyrotchBaseUrl, axiosBaseQuery } from "../axios/AxiosInstance";
import { createApi } from "@reduxjs/toolkit/query/react";
import { CapaRequest, files } from "../../models/Capa";
import { CapaResponse } from "../../models/Capa";
import { format } from "date-fns";

export interface ProductTypeCounts {
  productTypeName: string;
  productTypeId: number;
  total: number;
  close: number;
  live: number;
  delay: number;
}

export const CapaApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "CapaApi",
  tagTypes: ["Capa"],

  endpoints(build) {
    return {
      createCapa: build.mutation<
        any,
        {
          CapaRequest: any;
          files: File[];
        }
      >({
        query: ({ CapaRequest, files }) => {
          const formData = new FormData();

          formData.append("CapaRequest", JSON.stringify(CapaRequest));

          files.forEach((file) => {
            formData.append("files", file);
          });

          return {
            url: `capa/create`,
            method: "POST",
            data: formData,
            params: CapaRequest,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          };
        },
        invalidatesTags: ["Capa"],
      }),
      updateCapa: build.mutation<any, CapaRequest>({
        query: (CapaData) => ({
          url: `capa/update`,
          method: "PUT",
          data: CapaData,
        }),
        invalidatesTags: ["Capa"],
      }),
      // getCapas: build.query<CapaResponse, { page: number; size: number; search: string; status: string; userId: number; startDate: string; endDate: string }>({
      //   query: ({ page, size, search, status, userId, startDate, endDate}) => ({
      //     url: `capa?page=${page}&size=${size}&search=${search}&status=${status}&userId=${userId}&startDate=${startDate}&endDate=${endDate}`,
      //     method: "GET",
      //   }),
      //   providesTags: ["Capa"],
      // }),
      getCapas: build.query<CapaResponse, {
        page: number;
        size: number;
        search?: string;
        startDate?: string;
        endDate?: string;
        status?: string;
        userId?: number;
        productTypeIds?: any;
      }>({
        query: ({ page, size, search, startDate, endDate, status, userId, productTypeIds }) => {
          // Format dates if they exist
          const formattedStartDate = startDate ? format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : "";
          const formattedEndDate = endDate ? format(new Date(endDate), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : "";

          // Create an object with only parameters that have values
          const params: Record<string, string | number> = { page, size };
          if (search) params.search = search;
          if (startDate) params.startDate = formattedStartDate;
          if (endDate) params.endDate = formattedEndDate;
          if (status) params.status = status;
          if (productTypeIds) params.productTypeIds = productTypeIds;
          if (userId !== undefined) params.userId = userId;

          // Convert the params object into a query string
          const queryString = new URLSearchParams(params as Record<string, string>).toString();

          return {
            url: `capa?${queryString}`,
            method: "GET",
          };
        },
        providesTags: ["Capa"],
      }),
      deleteCapas: build.mutation<void, number>({
        query: (CapaId) => {
          return {
            url: `capa/delete/${CapaId}`,
            method: "DELETE",
          };
        },
        invalidatesTags: ["Capa"],
      }),

      requestApprovalCapa: build.mutation<
        any,
        { CapaId: number; remarks: string; files: File[] }
      >({
        query: ({ CapaId, remarks, files }) => {
          const formData = new FormData();

          // Append remarks as a string field
          formData.append("remarks", remarks);

          // Append files as binary data
          files.forEach((file) => {
            formData.append("files", file); // 'files' field to match the expected API parameter
          });

          return {
            url: `capa/sendForApproval/${CapaId}`, // Replace with the actual API endpoint
            method: "POST",
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data", // Required for FormData
            },
          };
        },
        invalidatesTags: ["Capa"], // Invalidate tags as per requirement
      }),

      completedCapa: build.mutation<any, number>({
        query: (CapaId) => {
          return {
            url: `Capas/${CapaId}/completed`,
            method: "Post",
          };
        },
        invalidatesTags: ["Capa"],
      }),
      approveDeclineCapa: build.mutation<
        any,
        { CapaId: number; approved: boolean; remarks: string }
      >({
        query: ({ CapaId, approved, remarks }) => ({
          url: `capa/approveOrDecline/${CapaId}`,
          method: "POST",
          data: {
            approved,
            remarks,
          },
        }),
        invalidatesTags: ["Capa"],
      }),
      productTypeCount: build.query<ProductTypeCounts[], void>({
        query: () => {
          return {
            url: `capa/product-type-counts`,
            method: "GET",
          };
        },
        providesTags: ["Capa"],
      }),

      sendToLiveData: build.mutation<any, number>({
        query: (capaId) => ({
          url: `capa/live/${capaId}`,
          method: "PUT",
        }),
        invalidatesTags: ["Capa"],
      }),

      importCapas: build.mutation<any, { file: File }>({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `capa/import`,
            method: "POST",
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          };
        },
        invalidatesTags: ["Capa"],
      }),

      exportCapas: build.mutation<Blob, {
        status?: "ASSIGNED" | "LIVE" | "SEND_FOR_APPROVAL" | "CLOSE";
        userId?: number;
        productTypeIds?: number[];
        startDate?: string;
        endDate?: string;
      }>({
        query: ({ status, userId, productTypeIds, startDate, endDate }) => {
          const params: Record<string, string | number> = {};
          
          if (status) params.status = status;
          if (userId !== undefined) params.userId = userId;
          if (startDate) params.startDate = startDate;
          if (endDate) params.endDate = endDate;
          
          // Handle productTypeIds array
          let queryString = new URLSearchParams(params as Record<string, string>).toString();
          
          if (productTypeIds && productTypeIds.length > 0) {
            const productTypeParams = new URLSearchParams();
            productTypeIds.forEach(id => productTypeParams.append('productTypeIds', id.toString()));
            const productTypeQuery = productTypeParams.toString();
            
            if (queryString) {
              queryString += `&${productTypeQuery}`;
            } else {
              queryString = productTypeQuery;
            }
          }
          
          const url = queryString ? `capa/export?${queryString}` : `capa/export`;
          
          return {
            url,
            method: "GET",
            responseHandler: (response: Response) => response.blob(),
          };
        },
      }),
    };
  },
});

export const {
  useCreateCapaMutation,
  useLazyGetCapasQuery,
  useDeleteCapasMutation,
  useUpdateCapaMutation,
  useRequestApprovalCapaMutation,
  useCompletedCapaMutation,
  useApproveDeclineCapaMutation,
  useProductTypeCountQuery,
  useLazyProductTypeCountQuery,
  useSendToLiveDataMutation,
  useImportCapasMutation,
  useExportCapasMutation
} = CapaApi;
